import React, { useMemo } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./contexts/AuthContext";
import { LocaleProvider } from "./i18n/LocaleProvider";
import { PublicLanguageChrome } from "./components/LanguageToggle";
import "./i18n";
import { ColorModeProvider, useColorMode } from "./contexts/ColorModeContext";
import { ExportProvider } from "./export";
import Layout from "./components/Layout";
import { Helmet } from "react-helmet-async";
import { createAppTheme } from "./theme";
import { queryClient } from "./queryClient";
import { productBrand } from "./productBrand";
import "./App.css";

function ThemedApp() {
  const { mode } = useColorMode();
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      <Helmet>
        <title>{productBrand.name}</title>
        <meta name="theme-color" content={productBrand.themeColor} />
        <link
          rel="icon"
          type={productBrand.faviconHref.endsWith(".png") ? "image/png" : "image/x-icon"}
          href={productBrand.faviconHref}
        />
        <link rel="apple-touch-icon" href={productBrand.faviconHref} />
      </Helmet>
      <Router>
        <AuthProvider>
          <LocaleProvider>
            <ExportProvider>
              <PublicLanguageChrome />
              <Layout />
            </ExportProvider>
          </LocaleProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ColorModeProvider>
        <ThemedApp />
      </ColorModeProvider>
    </QueryClientProvider>
  );
}

export default App;
